/* eslint-disable */
import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

// MUI Icons
import { Popover, Tabs, Tab } from '@mui/material';

// OpenLayers
import Geometry from 'ol/geom/Geometry';
import proj4 from 'proj4';
import { register as OlRegister } from 'ol/proj/proj4';
import OlLayerGroup from 'ol/layer/Group';
import OlSourceVector from 'ol/source/Vector';
import MapEvent from 'ol/MapEvent';
import { DrawEvent } from 'ol/interaction/Draw';
import BaseEvent from 'ol/events/Event';
import OlMapBrowserEvent from 'ol/MapBrowserEvent';
import OlBaseLayer from 'ol/layer/Base';
import OlFeature, { FeatureLike } from 'ol/Feature';
import OlPoint from 'ol/geom/Point';
import OlLineString from 'ol/geom/LineString';
import OlCollection from 'ol/Collection';
import OlStyle from 'ol/style/Style';
import OlStroke from 'ol/style/Stroke';
import OlCircle from 'ol/style/Circle';
import OlFill from 'ol/style/Fill';
import WKT from 'ol/format/WKT';
import OlVectorLayer from 'ol/layer/Vector';
import OlSourceTileWMS from 'ol/source/TileWMS';
import OlLayerTile from 'ol/layer/Tile';

// Custom Components
import Map from '@/components/Map/Map';
import {
  Controls,
  CoordZoomStatusControl,
  FullScreenControl,
  RotateControl,
  ScaleLineControl,
  ScaleRatioControl,
  StatusControl,
  ZoomControl,
  ZoomToExtentControl,
  GeoLocateControl,
  ZoomSliderControl,
  CenterMarkerControl,
  LayerTree,
  ViewHistoryControl,
} from '@/components/Map/Controls';
import {
  SidebarContent,
  SidebarControl,
  SidebarPane,
  SidebarTabs,
  SidebarTabList,
  SidebarTabListItem,
  SidebarHeading,
} from '@/components/Map/Controls/Sidebar';
import { InfoPane, MeasuresPane, MapViewsPane, LegendPane, CommentsPane, SearchPane, PrintPane } from '@/components/MapSidebarPanes';
import { Layers, VectorLayer } from '@/components/Map/Layers';
import { Overlays, PopupOverlay, MeasureTooltipOverlay } from '@/components/Map/Overlays';
import { Interactions, DefaultInteractions, DrawInteraction } from '@/components/Map/Interactions';
import {
  measurementsStyle,
  measuringStyle,
  selectedStyle,
  commentsStyle,
  commentingStyle,
  highlightedVectorStyle,
  elevationStyle,
} from '@/components/Map/mapStyles';
import GeoBaseLayerSwitcher from '@/components/Map/Controls/GEO/GeoBaseLayerSwitcher';
import GeoAPILayers from '@/components/Map/Layers/GEO/GeoAPILayers';
import GSInfoCard from '@/components/Map/InfoCards/GSInfoCard';
import useWindowSize, { Size } from '@/components/useWindowSize';
import GFISidebarControl from '@/components/Map/Controls/GFISidebar/GFISidebarControl';
import AttributeSearchPane from '@/components/MapSidebarPanes/AttributeSearchPane';
import DraggableInfoCardControl from '@/components/Map/Controls/Draggable/InfoCard/DraggableInfoCardControl';

// Views
import MiscCard from '@/views/Comments/MiscCard';
import HighlightAnimation from '@/views/Animations/HighlightAnimation';
import CommentDialog from '@/views/Comments/CommentDialog';

// Context
import UserContext from '@/context/UserContext/UserContext';
import LoaderContext from '@/context/LoaderContext/LoaderContext';
import SnackbarContext from '@/context/SnackbarContext/SnackbarContext';
import DialogContext from '@/context/DialogContext/DialogContext';
import TenantContext, { TenantContextType } from '@/context/TenantContext/TenantContext';

// Lib
import { flattenLayers } from '@/lib/olHelpers';
import DataController from '@/lib/DataController';
import Permissions from '@/lib/permissions';
import useApi from '@/lib/api/useApi';
import { helpers } from '@/lib/helpers';
import ppHelper from '@/lib/prostorniPlanoviHelper';

// Models
import modelComments from '@/models/comments';
import modelCommentsPhotos from '@/models/comments_photos';
import modelToponimi from '@/models/toponimi';
import modelToponimTipovi from '@/models/toponim_tipovi';

// React Query
import { useQueryClient } from '@tanstack/react-query';

// Services
import mapService from '@/services/mapService';
import gsService from '@/services/gsService';
import { authService } from '@/services/authService';

// Types
import { DCRecord } from '@/@types/lib/dataController';
import {
  IMapProps,
  CardPosition,
  OverlayCardType,
  IMiscCardOverlayData,
  IGFICardOverlayData,
  ICardOverlayData,
  IGFIOverlayFeatureIndex,
  GFIDisplayModeType,
} from '@/@types/views/map';
import { DrawingType, DrawingInteractionType, CoordinateSystemType, BoolSettings, StringSettings, ViewOptionsType, IMapView } from '@/@types/common/index';
import { GFIFeatureType, GFIResponseType } from '@/@types/services/gsService';
import EnumLayerFunctionality from '@/@types/services/gsServiceEnums';
import PPVInfoCard from '@/components/Map/InfoCards/PPVInfoCard';
import OlPolygon from 'ol/geom/Polygon';
import OlMultiPolygon from 'ol/geom/MultiPolygon';
import OlMultiLineString from 'ol/geom/MultiLineString';
import OlMultiPoint from 'ol/geom/MultiPoint';
import { Coordinate } from 'ol/coordinate';
import { useTheme } from '@mui/material';
import SwipeControl from '@/components/Map/Controls/Custom/SwipeControl/SwipeControl';
import BaseLayersLoader from '@/components/Map/Controls/Custom/BaseLayersLoader';
import SwipeLine from '@/components/Map/Controls/Custom/SwipeControl/SwipeLine';
import { ILayerSwiperState } from '@/@types/components/Map/Controls/Custom/LayerTree';
import LayersPane from '@/components/MapSidebarPanes/LayersPane';

const DEFAULT_VIEW_COORD_ZOOM = 20; // THIS IS FINE RIGHT?

const SWIPE_VALUE_KEY = 'swipe_value';
const SWIPE_STATE_KEY = 'swipe_state';
const GFI_DISPLAY_MODE_KEY = 'gfi_display_mode';

const getStoredGfiDisplayMode = (): GFIDisplayModeType => {
  const stored = localStorage.getItem(GFI_DISPLAY_MODE_KEY);
  return stored === 'sidebar' || stored === 'draggableCard' || stored === 'lockedCard' ? stored : 'lockedCard';
};

const MainMap = (props: IMapProps) => {
  const userContext = useContext(UserContext);
  const loaderContext = useContext(LoaderContext);
  const dialogContext = useContext(DialogContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const snackbarContext = useContext(SnackbarContext);
  const tenantContext = useContext(TenantContext);
  const apiInstance = useApi();
  const theme = useTheme();
  const queryClient = useQueryClient();

  const dcPrefetchTipovi = React.useMemo(() => new DataController(modelToponimTipovi), []);
  const dcPrefetchToponimi = React.useMemo(() => new DataController(modelToponimi), []);

  useEffect(() => {
    queryClient.prefetchQuery({
      queryKey: ['toponim-tipovi'],
      queryFn: async () => {
        const resp = await dcPrefetchTipovi.GetData();
        return resp.success && Array.isArray(resp.data) ? resp.data : [];
      },
    });
    queryClient.prefetchQuery({
      queryKey: ['toponimi-fe'],
      queryFn: async () => {
        const resp = await dcPrefetchToponimi.GetData();
        return resp.success && Array.isArray(resp.data) ? resp.data : [];
      },
    });
  }, []);

  const { map_id, local_storage_key = `viewsState_${userContext?.username}` } = props;

  const [mapInitialized, setMapInitialized] = useState(false);

  // Default values for initial view
  let viewsState: IMapView | undefined;

  // Values saved in localStorage
  const viewsStateJSON = localStorage.getItem(local_storage_key);
  if (viewsStateJSON !== null && viewsStateJSON !== undefined && JSON.parse(viewsStateJSON).views.length >= 1) {
    const viewsStateHolder = JSON.parse(viewsStateJSON);
    viewsState = viewsStateHolder.views[viewsStateHolder.currentViewIndex - 1];
  }

  const [viewOptions, setViewOptions] = useState<ViewOptionsType>({
    center: viewsState
      ? viewsState.center
      : tenantContext?.mapSettings &&
        tenantContext?.mapSettings.initial_view_center &&
        tenantContext?.mapSettings.initial_view_center.length === 2
      ? tenantContext.mapSettings.initial_view_center
      : [1731757, 5581737],
    zoom: viewsState
      ? viewsState.zoom
      : tenantContext?.mapSettings && tenantContext?.mapSettings.initial_view_zoom
      ? tenantContext.mapSettings.initial_view_zoom
      : 8,
    extent:
      tenantContext?.mapSettings && tenantContext?.mapSettings.max_extent && tenantContext?.mapSettings.max_extent.length > 0
        ? tenantContext.mapSettings.max_extent
        : undefined,
    // projection: wgs84PM,
    minZoom: tenantContext?.mapSettings && tenantContext?.mapSettings.map_min_view_zoom ? tenantContext.mapSettings.map_min_view_zoom : 7,
    maxZoom: tenantContext?.mapSettings && tenantContext?.mapSettings.map_max_view_zoom ? tenantContext.mapSettings.map_max_view_zoom : 20,
    constrainResolution: true,
  });

  const [searchParams] = useSearchParams();
  const [initialViewSet, setInitialViewSet] = useState(false);

  const getInitialCoordinate = useCallback(
    (key: string): number | undefined => {
      const value = searchParams.get(key);
      if (value === null) {
        return undefined;
      }

      const parsedValue = parseFloat(value);
      return Number.isNaN(parsedValue) ? undefined : parsedValue;
    },
    [searchParams]
  );

  const initialX = useMemo(() => getInitialCoordinate('x'), [searchParams]);
  const initialY = useMemo(() => getInitialCoordinate('y'), [searchParams]);
  const initialZoom = useMemo(() => getInitialCoordinate('zoom'), [searchParams]);

  useEffect(() => {
    if (initialX !== undefined && initialY !== undefined && mapInitialized && !initialViewSet) {
      const newView: IMapView = {
        center: [initialX, initialY],
        zoom: initialZoom ?? DEFAULT_VIEW_COORD_ZOOM,
      };
      setViewCenter(newView);
      setInitialViewSet(true);
    }
  }, [initialX, initialY, initialZoom, mapInitialized, initialViewSet]);

  const [elevationPointsSource, setElevationPointsSource] = useState<OlSourceVector<OlFeature<OlPoint>>>(new OlSourceVector({}));
  const elevationPointsFeatures = useMemo(() => elevationPointsSource.getFeatures(), [elevationPointsSource]);
  const [profileChartSettings, setProfileChartSettings] = useState<any>(undefined);

  const [drawingSource, setDrawingSource] = useState<OlSourceVector<OlFeature<Geometry>>>(new OlSourceVector({}));
  const [measurementsSource, setMeasurementsSource] = useState<OlSourceVector<OlFeature<Geometry>>>(new OlSourceVector({}));

  const [chosenCoordinateSystem, setChosenCoordinateSystem] = useState<number>(4326);
  const [layersCollection, setLayersCollection] = useState<OlCollection<OlBaseLayer> | undefined>(undefined);
  const [baseLayersGroup, setBaseLayersGroup] = useState<OlLayerGroup | undefined>(undefined);
  const [forceRefreshCounter, setForceRefreshCounter] = useState(0);
  const [drawType, setDrawType] = useState<DrawingType | undefined>(undefined);
  const [drawInteractionType, setDrawInteractionType] = useState<DrawingInteractionType | undefined>(undefined);
  const [isDrawingRoute, setIsDrawingRoute] = useState(false);
  const [feature, setFeature] = useState<OlFeature<Geometry> | undefined>(undefined);
  const [measuringFeature, setMeasuringFeature] = useState<OlFeature<Geometry> | undefined>(undefined);

  // TODO: make new state, array of objects containing GFICardOverlays and isDetached. Because we want to potentionally have more gsinfocards
  const [GFIData, setGFIData] = useState<IGFICardOverlayData[] | undefined>(undefined);
  const [gfiDisplayMode, setGfiDisplayMode] = useState<GFIDisplayModeType>(getStoredGfiDisplayMode);
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(false);

  const [miscCardOverlay, setMiscCardOverlay] = useState<IMiscCardOverlayData | undefined>(undefined);

  const [overlayTabActive, setOverlayTabActive] = useState<number>(0); // from layer dropdown on GSInfoCard
  const [overlayFeatureIndex, setOverlayFeatureIndex] = useState<IGFIOverlayFeatureIndex>({}); // from pagination on GSInfoCard
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const lastSelectedLayerRef = useRef<string | null>(null);

  const [defaultExtent, setDefaultExtent] = useState(
    tenantContext?.mapSettings?.default_extent && tenantContext?.mapSettings?.default_extent.length > 0
      ? tenantContext.mapSettings.default_extent
      : [1437016, 5271097, 2206278, 5860579]
  );

  // const [workingRecords, setWorkingRecords] = useState<DCRecord[]>([]);
  // const [workingSource, setWorkingSource] = useState<OlSourceVector<OlFeature<Geometry>>>(new OlSourceVector({}));

  // const [selectedRecords, setSelectedRecords] = useState<DCRecord[]>([]);
  const [selectedSource, setSelectedSource] = useState<OlSourceVector<OlFeature<Geometry>>>(new OlSourceVector({}));
  const [highlightedSource, setHighlightedSource] = useState<OlSourceVector<OlFeature<Geometry>>>(new OlSourceVector({}));
  // const [highlightedStyle, setHighlightedVectorStyle] = useState<StyleLike>(highlightedVectorStyle);

  const [commentRecords, setCommentRecords] = useState<DCRecord[]>([]);
  const [commentDrawType, setCommentDrawType] = useState<DrawingType | undefined>(undefined);
  const commentDrawId = useRef<string | undefined>(undefined);
  const [commentingFeature, setCommentingFeature] = useState<OlFeature<Geometry> | undefined>(undefined);

  /* const [commentsVisible, setCommentsVisible] = useState<boolean>(
    localStorage.getItem('maplayers') &&
      localStorage.getItem('maplayers') !== null &&
      // @ts-ignore
      JSON.parse(localStorage.getItem('maplayers'))
      ? // @ts-ignore
        JSON.parse(localStorage.getItem('maplayers'))['spatial-comments'] === true
      : false
  ); */

  // const [expandedPosjednici, setExpandedPosjednici] = useState(false);
  // const [expandedVlasnici, setExpandedVlasnici] = useState(false);

  const getSwipeValueFromStorage = (): number | null => {
    try {
      const parsed = JSON.parse(localStorage.getItem(SWIPE_VALUE_KEY) ?? 'null');
      return typeof parsed === 'number' && !isNaN(parsed) ? parsed : null;
    } catch {
      return null;
    }
  };
  const [swipeValue, setSwipeValue] = useState<number | null>(getSwipeValueFromStorage());

  // persist when swipeValue changes
  useEffect(() => {
    localStorage.setItem(SWIPE_VALUE_KEY, JSON.stringify(swipeValue));
  }, [swipeValue]);

  const getInitialSwiperState = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(SWIPE_STATE_KEY) ?? '{}');
      if (parsed && typeof parsed === 'object') {
        return parsed as ILayerSwiperState;
      }
      return {};
    } catch {
      return {};
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [layerSwipeState, setLayerSwipeState] = useState<ILayerSwiperState>(getInitialSwiperState());

  // persist when layerSwipeState changes
  useEffect(() => {
    localStorage.setItem(SWIPE_STATE_KEY, JSON.stringify(layerSwipeState));
  }, [layerSwipeState]);

  const zoomToDefaultExtentElementRef = useRef<HTMLLIElement>(null);
  const zoomToSelectedExtentElementRef = useRef<HTMLLIElement>(null);
  const fullScreenElementRef = useRef<HTMLLIElement>(null);
  const geoLocateElementRef = useRef<HTMLLIElement>(null);
  const [centerMarkerElement, setCenterMarkerElement] = useState<HTMLLIElement | null>(null);
  const centerMarkerElementCallback = useCallback((node: HTMLLIElement | null) => {
    setCenterMarkerElement(node);
  }, []);
  const layerSwitcherElementRef = useRef<JSX.Element>();

  // define proj
  proj4.defs(
    'EPSG:3765',
    '+proj=tmerc +lat_0=0 +lon_0=16.5 +k=0.9999 +x_0=500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  );
  proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs');
  proj4.defs(
    'EPSG:31276',
    '+proj=tmerc +pm=greenwich +lat_0=0 +lon_0=18 +k=0.9999 +x_0=6500000 +y_0=0 +ellps=bessel +towgs84=550.499,164.116,475.142,5.80967,2.07902,-11.62386,0.99999445824 +units=m +no_defs'
  );
  proj4.defs(
    'EPSG:3857',
    '+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs'
  );
  proj4.defs(
    'EPSG:8678',
    '+proj=tmerc +lat_0=0 +lon_0=18 +k=0.9999 +x_0=6500000 +y_0=0 +ellps=bessel +towgs84=682,-203,480,0,0,0,0 +units=m +no_defs +type=crs'
  );
  OlRegister(proj4);

  // const htrs96 = OlGetProjection('EPSG:3765');
  // const wgs84 = OlGetProjection('EPSG:4326');
  // const wgs84PM = OlGetProjection('EPSG:3857');

  // defaultViewCenter = [1731757, 5581737];
  // initialDefaultExtent = [1688674,5501166,1688774,5501266];
  // const defaultViewCenter = tenantContext?.mapSettings?.initial_view_center;
  // const initialDefaultExtent = tenantContext?.mapSettings?.default_extent;

  const dcKomentari = new DataController(modelComments);

  const size: Size = useWindowSize();

  useEffect(() => {
    getLayers();
    getBaseLayers();

    apiInstance.get('profile/params').then((resp) => {
      if (resp.success) {
        let layer = new OlLayerTile({
          source: new OlSourceTileWMS({
            url: process.env.REACT_APP_GEOSERVERPATH,
            params: {
              // @ts-ignore
              LAYERS: resp.data?.layers as string,
              FORMAT: 'image/png',
              TILED: true,
            },
          }),
          visible: true,
        });

        const data = {
          ...resp.data,
          layer: layer,
        };

        setProfileChartSettings(data);
      } else {
        console.log('DEM settings not found');
      }
    });
  }, [tenantContext?.id]);

  const getLayers = async () => {
    const coll = await mapService.getLayers(map_id);
    const parsedLayerCollection = new OlCollection<OlBaseLayer>(coll.getArray());
    // Conditionally add spatial comments layer
    if (userContext?.hasAnyPermission([Permissions.Private])) {
      const comments_layer = new OlVectorLayer({
        source: commentsSource,
        properties: {
          id: '_spatial-comments',
          title: 'map:layer.spatial-comments',
          ttoken: 'titles.spatial-comments',
          type: 'special',
          query: false,
          call_group: null,
          z_index: 900,
          style: commentsStyle,
        },
      });

      parsedLayerCollection.push(comments_layer);
    }

    // Conditionally add prostorni planovi layer
    if (userContext?.hasAnyPermission([Permissions.ProstorniPlanoviView]) && tenantContext) {
      const ppLayers = await ppHelper.getPPLayers(tenantContext.id);
      if (ppLayers) {
        parsedLayerCollection.push(ppLayers);
      }
    }

    // Now set the final collection and proceed
    setLayersCollection(parsedLayerCollection);
    setMapInitialized(true);
    getComments();
  };

  const getBaseLayers = () => {
    mapService
      .getBaseLayers(t, map_id, true)
      .then((olGroup: OlLayerGroup) => {
        setBaseLayersGroup(olGroup);
      })
      .catch((e) => {
        console.error('getBaseLayers failed', e);
      });
  };

  // getDefaultData() {
  //   mapService.getDefaultData(MAP_ID).then((data) => {
  //     if (data) {
  //       const viewData = Array.isArray(data) ? Object.assign({}, data[0]) : Object.assign({}, data);
  //       this.setState((prevState) => {
  //         return {
  //           ...prevState,
  //           viewOptions: {
  //             ...prevState.viewOptions,
  //             zoom: viewData.initial_view_zoom,
  //             center: viewData.initial_view_center
  //           },
  //           zoomToExtent: viewData.default_extent
  //         };
  //       });
  //     }
  //   });
  // }

  const getComments = () => {
    if (userContext?.hasAnyPermission([Permissions.Private])) {
      dcKomentari
        .GetData('comments/pane')
        .then((resp) => {
          if (resp.success && resp.data) {
            if (Array.isArray(resp.data)) {
              // const features = resp.data.map((x) => x.geom);
              const tempComments: DCRecord[] = [];
              for (const x of resp.data) {
                if (x.hasOwnProperty('geom') && x.geom && x.geom !== null) {
                  tempComments.push(x);
                }
              }
              setCommentRecords(tempComments);
            } else {
              setCommentRecords([]);
            }
          } else {
            setCommentRecords([]);
          }
        })
        .catch((resp) => {
          // console.log(resp);
          setCommentRecords([]);
        });
    }
  };

  const getPhotosForComment = (feature: FeatureLike, evt?: OlMapBrowserEvent<any>, coordinates?: [number, number]) => {
    let photos: any = [];

    const dc = new DataController(modelCommentsPhotos);

    dc.GetData(`comments/${String(Number(feature.getId()))}/photos`)
      .then((resp) => {
        if (resp.success && resp.data && resp.data !== null) {
          const features = [feature];
          if (Array.isArray(resp.data) && resp.data.length > 0) {
            photos = resp.data.slice();
            showCardOverlay(
              'commentinfo',
              {
                position: evt ? (evt.coordinate as CardPosition) : (coordinates as CardPosition),
                features,
                record: photos,
                type: 'commentinfo',
              },
              null
            );
          } else {
            showCardOverlay(
              'commentinfo',
              {
                position: evt ? (evt.coordinate as CardPosition) : (coordinates as CardPosition),
                features,
                type: 'commentinfo',
              },
              null
            );
          }
        }
      })
      .catch(() => {
        const features = [feature];
        showCardOverlay(
          'commentinfo',
          {
            position: evt ? (evt.coordinate as CardPosition) : (coordinates as CardPosition),
            features,
            type: 'commentinfo',
          },
          null
        );
      });
  };

  // For drawing comments
  const commentsSource = useMemo(() => {
    const commentsSource = new OlSourceVector();
    commentsSource.addFeatures(
      commentRecords.map((record) => {
        const feature = record.geom as OlFeature<Geometry>;
        feature.set('id', record.id);
        feature.set('_is_comment', true);
        return feature;
      })
    );
    return commentsSource;
  }, [commentRecords]);

  const setViewCenter = (view: IMapView) => {
    setViewOptions((prevState) => ({
      ...prevState,
      center: view.center,
      zoom: view.zoom,
    }));
  };

  const changeCoordinateSystemDisplay = (epsgCode: number) => {
    setChosenCoordinateSystem(epsgCode);
  };

  const handleClick = useCallback(
    (evt: OlMapBrowserEvent<any>) => {
      let hit = false;
      setSelectedSource(new OlSourceVector({}));

      // if (userContext && userContext.expireTime) {
      //   checkToken(userContext.expireTime);
      // }

      if (drawType || isDrawingRoute || commentDrawType) {
        return;
      }

      // on touch a tap only closes cards, info needs a long press (onLongpress)
      if (evt.type === 'click' && evt.originalEvent.pointerType === 'touch') {
        closeAllOverlays();
        return;
      }

      const clickedFeature = evt.map.forEachFeatureAtPixel(evt.pixel, (feature) => feature, {
        hitTolerance: 10,
      });

      closeAllOverlays();

      if (clickedFeature) {
        const id = clickedFeature.getId();
        if (clickedFeature.get('_is_comment') === true && id !== null && id !== undefined && commentDrawType === undefined) {
          // We clicked on comment
          getPhotosForComment(clickedFeature, evt);
          setSelectedSource(new OlSourceVector({ features: [clickedFeature as OlFeature<Geometry>] }));
          setHighlightedSource(new OlSourceVector({ features: [clickedFeature as OlFeature<Geometry>] }));
          // todo: in the future find way to combine this with GSInfoCard when clicking on comment with geom that also has GFI results, because currently this overrides GFI card with comment card, but ideally we would like to show both in the same card
          hit = true; 
        } else if (id !== null && id !== undefined) {
          // We clicked onto local feature (via WFS?), try to find corresponding layer and show GSInfoCard
          const featureIdStr = String(id);
          const layerKey = featureIdStr.split('.')[0];

          const item = localStorage.getItem('maplayers');
          const visibility = item ? (JSON.parse(item) as BoolSettings) : ({} as BoolSettings);
          const hiddenLayers = visibility ? Object.keys(visibility).filter((key) => visibility[key] === false) : [];
          const allLayers = layersCollection
            ? flattenLayers(layersCollection.getArray(), 5).filter((x) => !(x instanceof OlLayerGroup))
            : [];

          const gsLayers = allLayers.filter((l) => {
            const lid = l.get('id');
            return !hiddenLayers.includes(lid) && l.get('query') === true;
          });

          const layer = gsLayers.find((l) => {
            const parts = l.get('layer')?.split(':') ?? [];
            return parts.length > 1 ? parts[1] === layerKey : parts[0] === layerKey;
          });

          if (layer) {
            const olFeature = clickedFeature as OlFeature<
              OlPoint | OlLineString | OlPolygon | OlMultiPolygon | OlMultiLineString | OlMultiPoint
            >;

            const gfiFeature: GFIFeatureType = {
              id: featureIdStr,
              properties: olFeature.getProperties() as { [key: string]: string | number },
              geometry: {
                // warning: scuffed af
                type: (olFeature.getGeometry()?.getType() ?? 'Point') as GFIFeatureType['geometry']['type'],
                coordinates: (olFeature.getGeometry()?.getCoordinates() ?? []) as GFIFeatureType['geometry']['coordinates'],
              },
              geometry_name: 'geometry',
            };

            const overlayData: IGFICardOverlayData = {
              position: evt.coordinate as CardPosition,
              features: [gfiFeature],
              layerData: {
                layer: layerKey,
                model: layer?.get('model') ?? null,
                title: layer?.get('title') ?? layerKey,
                layerFunctionalityId: layer?.get('layer_functionality_id') ?? null,
                zIndex: layer?.get('z_index') ?? 0,
                sortorder: layer?.get('sortorder') ?? null,
              },
            };

            void showCardOverlay('gsinfo', overlayData, null);
            hit = false; // because we want to combine this with getFeatureInfo results, which will be shown in the same overlay
          }
        }
      }

      const timeBeforeLoader = 600; // time in miliseconds before loader appears while waiting on getFeatureInfo
      let loaderNeeded = true;
      let loading = false;

      function handleLoader() {
        if (loaderNeeded) {
          loaderContext.toggleLoading(true);
          loading = true;
        } else if (loading) {
          loading = false;
          loaderContext.toggleLoading(false);
        }
      }

      if (!hit) {
        const rememberedLayer = lastSelectedLayerRef.current;
        let rememberedLayerMatched = false;
        let nextGFIData: IGFICardOverlayData[] | undefined = undefined;

        setTimeout(() => {
          handleLoader();
        }, timeBeforeLoader);

        const item = localStorage.getItem('maplayers');
        const visibility = item ? (JSON.parse(item) as BoolSettings) : ({} as BoolSettings);
        const hiddenLayers = visibility ? Object.keys(visibility).filter((key) => visibility[key] === false) : [];
        const allLayers = layersCollection ? flattenLayers(layersCollection.getArray(), 5).filter((x) => !(x instanceof OlLayerGroup)) : [];
        const visibleLayers = allLayers.filter((x) => hiddenLayers.indexOf(x.get('id')) === -1);

        // dont query invisible layers!
        const currentZoom = evt.map.getView().getZoom();
        const isInZoomRange = (layer: OlBaseLayer) => {
          if (currentZoom === undefined) return true;
          let minZoom = layer.get('visible_min_zoom');
          if (minZoom !== undefined && minZoom > 0) {
            minZoom -= 1; // exclusive > inclusive limit, matches GeoLayer.tsx
          }
          const maxZoom = layer.get('visible_max_zoom');
          if (minZoom !== undefined && currentZoom <= minZoom) return false;
          if (maxZoom !== undefined && currentZoom > maxZoom) return false;
          return true;
        };

        // Step 1: Find all unique ID values for visible PP_ layers
        const visiblePPIds = new Set(
          allLayers
            .map((layer) => {
              const id = layer.get('id');
              const isVisible = !hiddenLayers.includes(id);

              // Match PP_<ID>_...
              const match = id && /^PP_(\d+)_/.exec(id);
              if (match && isVisible && isInZoomRange(layer)) {
                return match[1]; // ID
              }
              return null;
            })
            .filter(Boolean) // remove nulls
        );

        const gsLayersGroup: { [key: string]: OlBaseLayer[] } = { regular: [] };

        // Step 2: Build gsLayers list
        allLayers.forEach((layer) => {
          const id = layer.get('id');
          const isQueryable = layer.get('query') === true;
          const isVisible = !hiddenLayers.includes(id);
          const isVector = layer instanceof OlVectorLayer;

          const match = id && /^PP_(\d+)_/.exec(id);
          if (match && isQueryable && visiblePPIds.has(match[1])) {
            // Include if this PP_xxxx group has at least one visible layer
            if (!Object.hasOwn(gsLayersGroup, match[1])) {
              gsLayersGroup[match[1]] = [];
            }

            gsLayersGroup[match[1]].push(layer);
            return;
          }

          // For non-PP_ layers: only include if visible, queryable, and within its configured zoom range
          if (isVisible && isQueryable && !isVector && isInZoomRange(layer)) {
            gsLayersGroup['regular'].push(layer);
          }
        });

        const ppEntries = Object.entries(gsLayersGroup).filter(([k]) => k !== 'regular') as Array<[string, OlBaseLayer[]]>;
        const regularEntry = Object.entries(gsLayersGroup).find(([k]) => k === 'regular');

        const processGFIRequests = async () => {
          try {
            const ppCards = await ppHelper.getPPBatchOverlayData(evt.map, evt.pixel, ppEntries, evt.coordinate as CardPosition);

            if (ppCards.length > 0) {
              feature ? feature.setProperties({ selected: false }) : null;
              setFeature(undefined);

              // Prostorni planovi don't support the locked/draggable GSInfoCard, so force sidebar
              // for this overlay only - don't overwrite the user's remembered display mode preference
              setGfiDisplayMode('sidebar');
              setGFIData(ppCards);
              nextGFIData = ppCards;

              if (!rememberedLayerMatched && rememberedLayer) {
                const rememberedLayerIndex = ppCards.findIndex((value) => value.layerData.layer === rememberedLayer);
                if (rememberedLayerIndex !== -1) {
                  rememberedLayerMatched = true;
                  setOverlayTabActive(rememberedLayerIndex);
                }
              }
            }

            // Then, process regular layers only if no PP results were found
            if (!nextGFIData?.length && regularEntry) {
              const [_, gsLayers] = regularEntry;

              const timeItem = localStorage.getItem('layerstime');
              const layerTimes = timeItem ? (JSON.parse(timeItem) as StringSettings) : ({} as StringSettings);

              // one GFI request per (url, TIME) pair, since TIME applies to every layer in the request
              const gfiGroups = gsLayers.reduce((groups, layer) => {
                const url: string = layer.get('gfi_url_override') || layer.get('url') || '';
                const time: string | undefined = layer.get('timeseries') ? layerTimes[layer.get('id')] : undefined;
                const key = `${url}|${time ?? ''}`;
                if (!groups[key]) groups[key] = { url, time, layers: [] };
                groups[key].layers.push(layer);
                return groups;
              }, {} as Record<string, { url: string; time?: string; layers: OlBaseLayer[] }>);

              const gfiResponses = await Promise.all(
                Object.values(gfiGroups).map(async ({ url, time, layers }) => {
                  try {
                    return (await gsService.getFeatureInfo(evt.map, evt.pixel, layers, undefined, url || undefined, time)) ?? {};
                  } catch (err) {
                    console.error(`GFI call failed for ${url || 'internal'}`, err);
                    return {};
                  }
                })
              );

              const combinedResp = gfiResponses.reduce<GFIResponseType>((acc, result) => ({ ...acc, ...result }), {});

              if (combinedResp && Object.keys(combinedResp).length !== 0) {
                const layerKeys = Object.keys(combinedResp);

                if (layerKeys.some((k) => k.includes('photos360'))) {
                  const key = layerKeys.find((k) => k.includes('photos360'));
                  const data = {
                    position: evt.coordinate as CardPosition,
                    features: combinedResp[key || 'photos360'],
                    record: undefined,
                  };

                  showCardOverlay(
                    'photos360',
                    {
                      ...data,
                      type: 'photos360',
                    },
                    null
                  );
                } else {
                  const regularKeys = layerKeys.filter((k) => !k.startsWith('PP_') || k === 'PP_zahtjevi');

                  for (let index = 0; index < regularKeys.length; index++) {
                    const layerKey = regularKeys[index];
                    const data = {
                      position: evt.coordinate as CardPosition,
                      features: combinedResp[layerKey],
                      record: undefined,
                    };

                    let layer;
                    try {
                      layer = gsLayers.find((layer) => {
                        const layerParts = layer.get('layer').split(':');
                        if (layerParts.length > 1) {
                          return layerParts[1] === layerKey;
                        } else {
                          return layerParts[0] === layerKey;
                        }
                      });
                    } catch (error) {
                      console.error('GFI Layer Error', error);
                      return;
                    }

                    const overlayData = {
                      ...data,
                      layerData: {
                        layer: layerKey,
                        model: layer?.get('model'),
                        title: layer?.get('title'),
                        layerFunctionalityId: layer?.get('layer_functionality_id'),
                        zIndex: layer?.get('z_index') ?? 0,
                        sortorder: layer?.get('sortorder') ?? null,
                      },
                    };

                    const newCard = await showCardOverlay('gsinfo', overlayData, null);
                    if (newCard) {
                      nextGFIData = addCardToGFI(nextGFIData, newCard);
                    }
                  }

                  // Match after the loop: addCardToGFI re-sorts on every insertion, so matching mid-loop can pick a stale index.
                  if (!rememberedLayerMatched && rememberedLayer && nextGFIData) {
                    const rememberedLayerIndex = nextGFIData.findIndex((value) => value.layerData.layer === rememberedLayer);
                    if (rememberedLayerIndex !== -1) {
                      rememberedLayerMatched = true;
                      setOverlayTabActive(rememberedLayerIndex);
                    }
                  }
                }
              }
            }
          } catch (error) {
            console.error('Error processing GFI requests:', error);
          } finally {
            if (rememberedLayer && !rememberedLayerMatched) {
              lastSelectedLayerRef.current = null;
            }

            loaderNeeded = false;
            handleLoader();
          }
        };

        processGFIRequests();
      }
    },
    [drawType, layersCollection, commentDrawType, loaderContext]
  );

  const showCardOverlay = async (
    cardVariant: OverlayCardType,
    data: IGFICardOverlayData | IMiscCardOverlayData,
    ft: OlFeature<any> | null
  ): Promise<IGFICardOverlayData | undefined> => {
    // TODO: refactor
    feature ? feature.setProperties({ selected: false }) : null;

    if (ft) {
      ft.setProperties({ selected: true });
      setFeature(ft);
    } else {
      setFeature(undefined);
    }

    if (cardVariant === 'gsinfo') {
      const newData = data as IGFICardOverlayData;
      setGFIData((prev) => addCardToGFI(prev, newData));
      return newData;
    } else {
      setMiscCardOverlay(data as IMiscCardOverlayData);
      return undefined;
    }
  };

  const handleDockRight = () => {
    // User-initiated: remember this as the preferred display mode
    setGfiDisplayMode('sidebar');
    localStorage.setItem(GFI_DISPLAY_MODE_KEY, 'sidebar');
  };

  const closeGFIOverlay = () => {
    setGFIData(undefined);
    // Restore the remembered preference (prostorni planovi may have forced 'sidebar' for this overlay only, without persisting it)
    setGfiDisplayMode(getStoredGfiDisplayMode());
  };

  const closeMiscOverlay = () => {
    setMiscCardOverlay(undefined);
  };

  const closeAllOverlays = () => {
    closeGFIOverlay();
    closeMiscOverlay();
    setOverlayFeatureIndex({});
    setOverlayTabActive(0);
  };

  const handleCloseSidebar = () => {
    closeAllOverlays();
  };

  const handleOverlayTabChange = (index: number) => {
    setOverlayTabActive(index);
    lastSelectedLayerRef.current = GFIData?.[index]?.layerData.layer ?? null;
  };

  const addCardToGFI = (GFI: IGFICardOverlayData[] | undefined, newCard: IGFICardOverlayData): IGFICardOverlayData[] => {
    const newGFI = GFI ? [...GFI, newCard] : [newCard];

    const ppCards = newGFI.filter((c) => c.type === 'pp');
    const regularCards = newGFI.filter((c) => c.type !== 'pp');

    // Order by ui_sortorder (ascending, matching the admin/baselayers convention) when set;
    // layers without one fall back to the old zIndex-desc order, after the sorted ones.
    const byLayerOrder = (a: IGFICardOverlayData, b: IGFICardOverlayData) => {
      const sortA = a.layerData.sortorder;
      const sortB = b.layerData.sortorder;
      if (sortA != null && sortB != null) {
        return sortA - sortB;
      }
      if (sortA != null) return -1;
      if (sortB != null) return 1;
      return (b.layerData.zIndex ?? 0) - (a.layerData.zIndex ?? 0);
    };

    return [...ppCards.sort(byLayerOrder), ...regularCards.sort(byLayerOrder)];
  };

  const handleViewChangeCenter = useCallback((evt: MapEvent) => {
    if (evt && evt.map) {
      const newView = {
        center: evt.map.getView().getCenter(),
        zoom: evt.map.getView().getZoom(),
      };
      setViewOptions(Object.assign(viewOptions, newView));
    }
  }, []);

  const handleSidebarPaneChange = useCallback(
    (id: string) => {
      if (drawType && id && id !== 'measures') {
        setDrawType(undefined);
        setDrawInteractionType(undefined);
        setMeasuringFeature(undefined);
      } else if (id) {
        if (size && size.width && size.width > 768) {
          setDrawType(undefined);
          setDrawInteractionType(undefined);
          setMeasuringFeature(undefined);
        }
      }
    },
    [drawType]
  );

  const changeDrawType = (type: DrawingType) => {
    if (drawType !== type) {
      setDrawType(type);
      setDrawInteractionType(type === 'Profile' ? 'LineString' : type);
      setMeasuringFeature(undefined);
      if (type === 'Profile') {
        handleEraseMeasurementsWithoutDrawType();
      }
    } else {
      setDrawType(undefined);
      setDrawInteractionType(undefined);
      setMeasuringFeature(undefined);
    }
  };

  const handleDrawMeasureStart = (evt: DrawEvent) => {
    if (drawingSource.getFeatures().length > 0) {
      setDrawingSource(new OlSourceVector({}));
    }

    setMeasuringFeature(evt.feature);
  };

  const handleDrawMeasureEnd = (evt: DrawEvent) => {
    setDrawingSource(new OlSourceVector({}));
    const feat = evt.feature;
    feat.setId(`_measurement.${helpers.createUUID()}`);
    feat.set('display_crs', chosenCoordinateSystem);
    if (drawType === 'Profile') {
      handleEraseMeasurementsWithoutDrawType();
      setMeasurementsSource(
        (prevState) =>
          new OlSourceVector({
            features: [evt.feature],
          })
      );
    } else {
      setMeasurementsSource(
        (prevState) =>
          new OlSourceVector({
            features: [...prevState.getFeatures(), evt.feature],
          })
      );
    }

    if (drawType === 'Point' || drawType === 'Profile') {
      if (profileChartSettings) {
        // @ts-ignore
        getProfilePoints(evt.target.getMap() as OlMap, feat);
      }
    }
    setMeasuringFeature(undefined);
    setForceRefreshCounter((prevState) => (prevState ? prevState + 1 : 1));
  };

  const getProfilePoints = (map: any, feat: OlFeature<OlPoint | OlLineString>) => {
    const geom = feat.getGeometry();
    const feat_uuid = helpers.createUUID();

    try {
      // warning: pierro code ahead
      if (geom) {
        if (geom instanceof OlPoint) {
          const p = geom.getCoordinates();
          const pxPoint = map.getPixelFromCoordinate(p);
          profileChartSettings &&
            gsService.getFeatureInfo(map, pxPoint, [profileChartSettings.layer], profileChartSettings.layers).then((resp) => {
              const feat = parseAltResponse(resp, p);
              if (feat) {
                setElevationPointsSource(
                  (prevState) =>
                    new OlSourceVector({
                      features: prevState.getFeatures().concat([feat]),
                    })
                );
                setForceRefreshCounter((prevState) => (prevState ? prevState + 1 : 1));
              }
            });
        } else if (geom instanceof OlLineString) {
          // coordinates in View projection
          const coords = geom.getCoordinates();
          // but we need pixel coordinates on a map
          const pxPoints = coords.map((x) => map.getPixelFromCoordinate(x));
          // this can take some time so we turn on loader
          loaderContext.toggleLoading(true);
          // we call all GFI calls and continue when all responses are received (to calculcate dh we need to make sure that previous point is already received)
          Promise.all(
            pxPoints.map((px) =>
              Promise.resolve(
                profileChartSettings && gsService.getFeatureInfo(map, px, [profileChartSettings.layer], profileChartSettings.layers)
              )
            )
          )
            .then((responses) => {
              if (
                (responses as Array<any>).some((response) => {
                  return !(response?.dem && Array.isArray(response.dem));
                })
              ) {
                console.error('Profile points out of DEM layer bounds');
                return;
              }

              // create features from responses
              const features = responses.map((resp, index) =>
                parseAltResponse(resp, coords[index], feat_uuid, index, index > 0 ? getAlt(responses[index - 1]) : undefined)
              );
              // filter null features if any
              const validFeatures = features.filter((x) => x !== null) as OlFeature<OlPoint>[];
              // update source with all values at once
              setElevationPointsSource(
                (prevState) =>
                  new OlSourceVector({
                    features: prevState.getFeatures().concat(validFeatures),
                  })
              );
              // force refresh
              setForceRefreshCounter((prevState) => (prevState ? prevState + 1 : 1));
            })
            .finally(() => {
              // when everything is ready, we turn off loader
              loaderContext.toggleLoading(false);
            });
        }
      }
    } catch (error) {
      snackbarContext.showNotification(t('messages.profile_layer_issue') as string, 'error');
      console.error('Error while creating profile', error);
      return;
    }
  };

  const getAlt = (resp: GFIResponseType | null) => {
    const gray_index = resp ? ((resp.dem[0].properties.DEM || resp.dem[0].properties.GRAY_INDEX) as number) : undefined;

    const alt = resp ? gray_index : undefined; // Ugly because response returns an object with key "" which containes an array
    if (typeof alt === 'number' && (alt < -413 || alt > 8848)) return undefined; // Check if elevation data makes sense or its dem shenanigans
    return resp ? gray_index : undefined;
  };

  const parseAltResponse = (resp: GFIResponseType | null, point: number[], feat_uuid?: string, ord?: number, prevAlt?: number) => {
    if (resp && Object.keys(resp).length !== 0) {
      const gray_index = resp ? ((resp.dem[0].properties.DEM || resp.dem[0].properties.GRAY_INDEX) as number) : undefined;
      if (!gray_index) return null;
      // @ts-ignore
      const alt: number = gray_index; // Ugly because response returns an object with key "" which containes an array
      if (alt < -413 || alt > 8848) return null; // Check if elevation data makes sense or its dem shenanigans
      const newFeat = new OlFeature({
        geometry: new OlPoint(point),
      });
      const altNum = Number(gray_index);
      if (isNaN(altNum)) return null; // just in case

      newFeat.set('alt', altNum.toFixed(2));
      if (feat_uuid) {
        newFeat.set('line_uuid', feat_uuid);
      }
      if (ord !== undefined) {
        newFeat.set('ord', ord);
        // console.log('got response for ord: ', ord);
      }

      if (prevAlt !== undefined) {
        newFeat.set('dh', (altNum - prevAlt).toFixed(2));
      } else {
        newFeat.set('dh', 'N/A');
      }

      return newFeat;
    }
    return null;
  };

  const handleDrawMeasureChange = (evt: BaseEvent) => {
    // console.log("handleDrawChange", evt);
  };

  const handleEraseMeasurements = () => {
    // setDrawType(undefined);
    setMeasurementsSource(new OlSourceVector({}));
    setMeasuringFeature(undefined);
    setElevationPointsSource(new OlSourceVector({}));
  };

  const handleEraseMeasurementsWithoutDrawType = () => {
    setMeasurementsSource(new OlSourceVector({}));
    setElevationPointsSource(new OlSourceVector({}));
  };

  const handleLayerVisibilityChange = useCallback(() => {
    setForceRefreshCounter((prevState) => prevState + 1);
  }, []);

  const onToggleDrawRoute = (isActive: boolean) => {
    setIsDrawingRoute(isActive);
  };

  // function checkToken(expireTime: Moment) {
  //   const isExpired = expireTime < moment();
  //   if (isExpired) {
  //     authService.logout(tenantContext as TenantContextType);
  //     snackbarContext.showNotification(t('messages.token_expired'), 'warning');
  //     navigate('/login');
  //   }
  // }

  const changeCommentDrawType = useCallback(
    (type: DrawingType) => {
      if (commentDrawType !== type) {
        setCommentDrawType(type);
        setCommentingFeature(undefined);
      } else {
        setCommentDrawType(undefined);
        setCommentingFeature(undefined);
      }
    },
    [commentDrawType]
  );

  const handleDrawCommentsStart = (evt: DrawEvent) => {
    if (drawingSource.getFeatures().length > 0) {
      setDrawingSource(new OlSourceVector({}));
    }

    setCommentingFeature(evt.feature);
  };

  const handleDrawCommentsEnd = (evt: DrawEvent) => {
    setTimeout(() => {
      const newFeature = evt.feature;
      setDrawingSource(new OlSourceVector({}));

      const format = new WKT();

      const dialogData: any = {
        dc: dcKomentari,
        form: 'insert',
        mode: 'insert',
        initialRecord: {
          geom: newFeature,
          extent: newFeature.getGeometry()?.getExtent(),
        },
        refreshLayers: getLayers,
        onClose: () => {
          setCommentDrawType(undefined);
          setCommentingFeature(undefined);
          getComments();
        },
      };

      commentDrawId.current = undefined;
      dialogContext.showDialog(CommentDialog, dialogData);
      setForceRefreshCounter((prevState) => (prevState ? prevState + 1 : 1));
    }, 1000);
  };

  const handleDrawCommentsChange = (evt: BaseEvent) => {
    // console.log("handleDrawChange", evt);
  };

  /* const closeOverlays = (callback: any) => {
    setMiscCardOverlay(undefined);
    // setExpandedPosjednici(false);
    // setExpandedVlasnici(false);
    if (callback !== undefined && typeof callback === 'function') {
      callback();
    }
  }; */

  const handlePopoverOverlayClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const customSelectedStyle = () => {
    const highlightColor = theme.palette.primary.main;

    const style = new OlStyle({
      image: new OlCircle({
        radius: 7,
        stroke: new OlStroke({
          color: 'rgba(255, 255, 255, 0.79)',
        }),
        fill: new OlFill({
          color: highlightColor,
        }),
      }),
      fill: new OlFill({
        color: 'rgba(184, 184, 184, 0.4)',
      }),
      stroke: new OlStroke({
        color: highlightColor,
        width: 3.25,
      }),
    });
    return style;
  };

  useEffect(() => {
    if (GFIData !== undefined && highlightedSource.getFeatures().length !== 0) {
      setGFIData(undefined);
    }
    if (miscCardOverlay !== undefined && highlightedSource.getFeatures().length !== 0) {
      setMiscCardOverlay(undefined);
    }
  }, [highlightedSource]);

  useEffect(() => {
    if (!GFIData) {
      setSelectedSource(new OlSourceVector());
      return;
    }

    const activeLayer = GFIData[overlayTabActive];
    if (!activeLayer) return;

    let featureIndex = overlayFeatureIndex[activeLayer.layerData.layer];

    if (!featureIndex) featureIndex = 0;

    const featureData = activeLayer.features[featureIndex];

    let geometry;

    switch (featureData?.geometry?.type) {
      case 'Point':
        geometry = new OlPoint(featureData.geometry.coordinates as Coordinate);
        break;
      case 'MultiPoint':
        geometry = new OlMultiPoint(featureData.geometry.coordinates as Coordinate);
        break;
      case 'LineString':
        geometry = new OlLineString(featureData.geometry.coordinates as Coordinate);
        break;
      case 'MultiLineString':
        geometry = new OlMultiLineString(featureData.geometry.coordinates as Coordinate);
        break;
      case 'Polygon':
        geometry = new OlPolygon(featureData.geometry.coordinates as Coordinate);
        break;
      case 'MultiPolygon':
        geometry = new OlMultiPolygon(featureData.geometry.coordinates as Coordinate);
        break;
      default:
        geometry = new OlPoint(activeLayer.position as Coordinate);
        console.warn('Unsupported geometry type (will default to clicked position):', featureData?.geometry?.type);
    }

    const newFeature = new OlFeature({
      geometry: geometry,
      id: featureData.id,
      properties: featureData.properties,
    });

    setSelectedSource(new OlSourceVector({ features: newFeature ? [newFeature] : undefined }));
  }, [GFIData, overlayFeatureIndex, overlayTabActive]);

  const handleSetDetach = (newState: boolean) => {
    // User-initiated: remember this as the preferred display mode
    const mode: GFIDisplayModeType = newState ? 'draggableCard' : 'lockedCard';
    setGfiDisplayMode(mode);
    localStorage.setItem(GFI_DISPLAY_MODE_KEY, mode);
  };

  const hasGlobalSearch = tenantContext && tenantContext !== null && tenantContext?.canSearch ? true : false;
  const hasAttributeSearch =
    map_id === 1
      ? tenantContext && tenantContext !== null && tenantContext?.canAttributeSearch
      : tenantContext && tenantContext !== null && tenantContext?.canAttributeSearchPublic;

  const handleRightSidebarCollapseChange = (newState: boolean) => {
    setIsRightSidebarOpen(!newState);
  };

  const handleSetOverlayFeatureIndex = (layer: string, index: number) => {
    setOverlayFeatureIndex((prev) => ({
      ...prev,
      [layer]: index,
    }));
  };

  const allowSwiperSlider = useMemo(() => !(swipeValue === undefined || swipeValue === null), [swipeValue]);

  // Memoized Layer Tree cause unnecessary rerendering was causing problems
  const MemoizedLayerPane = useMemo(() => {
    return (
      <LayersPane
        mapId={map_id}
        ready={!!layersCollection}
        layersCollection={layersCollection as OlCollection<OlBaseLayer>}
        baseLayersGroup={baseLayersGroup}
        onLayerVisibilityChange={handleLayerVisibilityChange}
        setSwipeValue={setSwipeValue}
        swipeState={layerSwipeState}
        setLayerSwipeState={setLayerSwipeState}
        allowSwiperSlider={allowSwiperSlider}
      />
    );
  }, [map_id, layersCollection, baseLayersGroup, setSwipeValue, handleLayerVisibilityChange, layerSwipeState, setLayerSwipeState, allowSwiperSlider]);

  if (userContext && layersCollection) {
    return (
      <Map
        mapId={map_id}
        height="800px"
        view={viewOptions}
        onClick={handleClick}
        onLongpress={handleClick}
        onMoveend={handleViewChangeCenter}
        className={'sidebar-map'} //{`sidebar-map ${(gfiDisplayMode === 'sidebar' || gfiDisplayMode === 'prostorni_planovi') ? (isRightSidebarOpen ? 'sidebar-right-open' : 'sidebar-right-collapsed') : 'sidebar-right-none'}
        id="main-map"
        initialized={mapInitialized}
        moveTolerance={10}
        maxTilesLoading={10}
      >
        <Controls>
          <DraggableInfoCardControl
            GFICardOverlay={GFIData}
            overlayTabActive={overlayTabActive}
            closeGFIOverlay={closeGFIOverlay}
            currentIndex={overlayFeatureIndex}
            setIndex={setOverlayFeatureIndex}
            handlePopoverOverlayClick={handlePopoverOverlayClick}
            isDetached={gfiDisplayMode === 'draggableCard'}
            handleSetDetach={handleSetDetach}
            onDockRight={handleDockRight}
          />
          <CoordZoomStatusControl chosenCoordinateSystem={chosenCoordinateSystem} />
          <StatusControl changeCoordinateSystem={changeCoordinateSystemDisplay} />
          <ZoomControl stickySidebar zoomInTipLabel={`${t('map:controls.zoom_in')}`} zoomOutTipLabel={`${t('map:controls.zoom_out')}`} />
          <ZoomSliderControl />
          <ScaleRatioControl ppi={96} />
          <ScaleLineControl />
          <RotateControl autoHide={false} tipLabel={`${t('map:controls.reset_north')}`} />
          {fullScreenElementRef?.current && mapInitialized ? (
            <FullScreenControl
              id="full-screen"
              tipLabel={`${t('map:controls.full_screen')}`}
              target={fullScreenElementRef?.current}
              className="ol-sidebar-control"
            />
          ) : null}

          {zoomToDefaultExtentElementRef?.current && mapInitialized ? (
            <ZoomToExtentControl
              isSidebar
              id="zoom-extent-default"
              target={zoomToDefaultExtentElementRef.current}
              extent={defaultExtent}
              tipLabel={`${t('map:controls.zoom_to_extent')}`}
              className="ol-sidebar-control"
            />
          ) : null}
          {geoLocateElementRef?.current && mapInitialized ? (
            <GeoLocateControl
              id="geo-locate"
              tooltip={`${t('map:controls.geolocate')}`}
              target={geoLocateElementRef.current}
              className="ol-sidebar-control"
            />
          ) : null}
          {centerMarkerElement && mapInitialized ? (
            <CenterMarkerControl
              id="center-marker"
              tooltip={`${t('map:controls.center_marker')}`}
              target={centerMarkerElement}
              className="ol-sidebar-control"
            />
          ) : null}
          <ViewHistoryControl local_storage_key={local_storage_key} />
          <BaseLayersLoader baseLayersGroup={baseLayersGroup} />
          {/* <GeoBaseLayerSwitcher allowNoLayer /> */}
          <SidebarControl
            id="sidebar-left"
            position="left"
            initialOpenId="layers"
            initialOpenHeading={'map:sidebar.layers' as string}
            onTabChange={handleSidebarPaneChange}
          >
            <SidebarHeading />
            <SidebarTabs>
              <SidebarTabList>
                <SidebarTabListItem
                  id="info"
                  title={`${'map:sidebar.info'}`}
                  icon={<i className="fas fa-info-circle" />}
                  needPermission={[Permissions.Core]}
                />
                <SidebarTabListItem
                  id="layers"
                  title={`${'map:sidebar.layers'}`}
                  icon={<i className="fas fa-layer-group" />}
                  needPermission={[Permissions.Core]}
                />
                {/* <SidebarTabListItem
                  id="legend"
                  title={'map:sidebar.legend'}
                  icon={<i className="fas fa-list-alt" />}
                  needPermission={[Permissions.Core]}
                /> */}
                {hasGlobalSearch ? (
                  <SidebarTabListItem
                    id="search"
                    title={'map:sidebar.search'}
                    icon={<i className="fas fa-search"></i>}
                    needPermission={[Permissions.Core]}
                  />
                ) : null}
                {hasAttributeSearch ? (
                  <SidebarTabListItem
                    id="attribute-search"
                    title={'map:sidebar.attribute_search'}
                    icon={<i className="fas fa-filter"></i>}
                    needPermission={[Permissions.Core]}
                  />
                ) : null}
                <SidebarTabListItem
                  id="mapviews"
                  title={`${'map:sidebar.map_views'}`}
                  icon={<i className="fas fa-bookmark" />}
                  needPermission={[Permissions.Private]}
                />
                <SidebarTabListItem
                  id="comments"
                  title={`${'map:sidebar.comments'}`}
                  icon={<i className="fas fa-comment" />}
                  needPermission={[Permissions.Private]}
                />
                <SidebarTabListItem
                  id="measures"
                  title={`${'map:sidebar.measures'}`}
                  icon={<i className="fas fa-pencil-ruler" />}
                  needPermission={[Permissions.Core]}
                  onSidebarCollapse={() => {
                    handleSidebarPaneChange('measures');
                  }}
                />
                <li ref={zoomToDefaultExtentElementRef} />
                <li ref={fullScreenElementRef} />
                <li ref={geoLocateElementRef} />
                <li id="center-marker-container" ref={centerMarkerElementCallback} />
                <SidebarTabListItem
                  id="print"
                  title={`${'map:sidebar.print'}`}
                  icon={<i className="fas fa-print" />}
                  needPermission={[Permissions.Core]}
                  onSidebarCollapse={() => {
                    handleSidebarPaneChange('print');
                  }}
                />
              </SidebarTabList>
            </SidebarTabs>
            <SidebarContent>
              <SidebarPane id="info">
                <InfoPane />
              </SidebarPane>
              <SidebarPane id="layers" limitHeight noPadding>
                {MemoizedLayerPane}
              </SidebarPane>
              {/* <SidebarPane id="legend">
                <LegendPane layersCollection={layersCollection} />
              </SidebarPane> */}
              {hasGlobalSearch ? (
                <SidebarPane noPadding limitHeight id="search">
                  <SearchPane setSelectedSource={setSelectedSource} searchItemsListDisplayLimit={20} />
                </SidebarPane>
              ) : null}
              {hasAttributeSearch ? (
                <SidebarPane noPadding limitHeight id="attribute-search">
                  <AttributeSearchPane setSelectedSource={setSelectedSource} />
                </SidebarPane>
              ) : null}

              <SidebarPane id="mapviews" needPermission={[Permissions.Private]}>
                <MapViewsPane />
              </SidebarPane>
              <SidebarPane id="comments" needPermission={[Permissions.Private]}>
                <CommentsPane
                  // toggleVisibility={() => {
                  //   setCommentsVisible((prev) => !prev);
                  // }}
                  // visible={commentsVisible}
                  changeDrawType={changeCommentDrawType} // changeCommentDrawType
                  drawType={commentDrawType} // commentDrawType
                  records={commentRecords} // commentRecords
                  setSelectedSource={(x) => {
                    setHighlightedSource(x);
                    setSelectedSource(x);
                  }}
                />
              </SidebarPane>
              <SidebarPane id="measures">
                <MeasuresPane
                  // toggleDraw={handleToggleDrawMeasure} TODO: implement method handleToggleDrawMeasure in this component
                  changeDrawType={changeDrawType}
                  handleEraseMeasurements={handleEraseMeasurements}
                  drawType={drawType}
                  elevationPointsFeatures={elevationPointsFeatures}
                  measurementsSource={measurementsSource}
                  profileSettings={profileChartSettings}
                  layersCollection={layersCollection}
                />
              </SidebarPane>
              <SidebarPane id="print">
                <PrintPane mapId={map_id} />
              </SidebarPane>
            </SidebarContent>
          </SidebarControl>

          <GFISidebarControl
            gfiDisplayMode={gfiDisplayMode}
            gfiResults={GFIData ?? []}
            onSidebarCollapse={handleRightSidebarCollapseChange}
            overlayFeatureIndex={overlayFeatureIndex}
            setOverlayFeatureIndex={handleSetOverlayFeatureIndex}
            onSidebarClose={handleCloseSidebar}
            overlayTabActive={overlayTabActive}
            setOverlayTabActive={handleOverlayTabChange}
            onUndock={() => handleSetDetach(false)}
          />
          <HighlightAnimation features={highlightedSource.getFeatures()} setSource={setHighlightedSource} />
          {swipeValue !== null ? <SwipeControl swipeValue={swipeValue} setSwipeValue={setSwipeValue} /> : null}
        </Controls>
        <Layers>
          {layersCollection ? (
            <Layers>
              <GeoAPILayers layersCollection={layersCollection} swipeValue={swipeValue} swipeState={layerSwipeState} />
              <VectorLayer
                id="_measurements"
                source={measurementsSource}
                style={(f) => measurementsStyle(f, chosenCoordinateSystem)}
                zIndex={900}
              />
              <VectorLayer id="_selected" source={selectedSource} style={customSelectedStyle} zIndex={910} />
              <VectorLayer
                id="_spatial-comments"
                swipeState={layerSwipeState['_spatial-comments']}
                swipeValue={swipeValue}
                source={commentsSource}
                style={commentsStyle}
                zIndex={905}
              />
              <VectorLayer id="_elevation_points" source={elevationPointsSource} style={elevationStyle} zIndex={901} />
            </Layers>
          ) : null}
        </Layers>
        <Overlays>
          {swipeValue !== null ? <SwipeLine swipeValue={swipeValue} /> : null}

          <PopupOverlay
            id="feature-overlay"
            position={
              gfiDisplayMode === 'lockedCard' && GFIData && GFIData[overlayTabActive] ? GFIData[overlayTabActive].position : undefined
            }
            autoPan
            onClose={() => {}}
          >
            {GFIData && GFIData.length > 1 ? (
              <Popover
                id="simple-popover"
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
              >
                <Tabs
                  orientation="vertical"
                  variant="scrollable"
                  value={overlayTabActive}
                  onChange={(event: React.SyntheticEvent, newValue: number) => {
                    handleOverlayTabChange(newValue);
                    setAnchorEl(null);
                  }}
                >
                  {GFIData.map((value, index) => (
                    <Tab key={'layer-tab-' + index} label={t(value.layerData.title)} id={value.layerData.layer} />
                  ))}
                </Tabs>
              </Popover>
            ) : (
              <></>
            )}

            {GFIData && GFIData.length > overlayTabActive ? (
              GFIData[overlayTabActive]?.layerData?.layerFunctionalityId === EnumLayerFunctionality.cjenovni_blok_custom_actions ? (
                <PPVInfoCard
                  featureData={GFIData[overlayTabActive]}
                  onClose={closeGFIOverlay}
                  layerCount={GFIData.length}
                  setIndex={setOverlayFeatureIndex}
                  currentIndex={overlayFeatureIndex}
                  handlePopoverOverlayClick={handlePopoverOverlayClick}
                />
              ) : gfiDisplayMode === 'lockedCard' ? (
                <GSInfoCard
                  isDetached={false}
                  handleSetDetach={handleSetDetach}
                  featureData={GFIData[overlayTabActive]}
                  onClose={closeGFIOverlay}
                  layerCount={GFIData.length}
                  setIndex={setOverlayFeatureIndex}
                  currentIndex={overlayFeatureIndex}
                  handlePopoverOverlayClick={handlePopoverOverlayClick}
                  onDockRight={handleDockRight}
                />
              ) : (
                <></>
              )
            ) : (
              <></>
            )}
          </PopupOverlay>
          <PopupOverlay id="comment-overlay" position={miscCardOverlay ? miscCardOverlay.position : undefined} autoPan onClose={() => {}}>
            {miscCardOverlay ? (
              <MiscCard
                featureData={miscCardOverlay}
                onClose={closeMiscOverlay}
                onCommentDelete={getComments}
                refreshLayers={getComments}
                refreshPhotos={getPhotosForComment}
              />
            ) : null}
          </PopupOverlay>

          <MeasureTooltipOverlay id="measure-overlay" feature={measuringFeature} position={undefined} />
        </Overlays>
        <Interactions>
          <DefaultInteractions />
          {drawInteractionType ? (
            <DrawInteraction
              source={drawingSource}
              type={drawInteractionType}
              style={measuringStyle}
              onChange={handleDrawMeasureChange}
              onDrawstart={handleDrawMeasureStart}
              onDrawend={handleDrawMeasureEnd}
            />
          ) : null}
          {commentDrawType ? (
            <DrawInteraction
              source={drawingSource}
              type={commentDrawType}
              style={commentingStyle}
              onChange={handleDrawCommentsChange}
              onDrawstart={handleDrawCommentsStart}
              onDrawend={handleDrawCommentsEnd}
            />
          ) : null}
        </Interactions>
      </Map>
    );
  }
  return null;
};

export default MainMap;
